import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHeart, FaComment, FaEye, FaThumbtack } from 'react-icons/fa';
import RoleBadge from './RoleBadge';
import VerifiedBadge from './VerifiedBadge';

interface ForumPostCardProps {
    news: {
        id: string;
        title: string;
        content: string;
        category: string;
        author: {
            id: string;
            name: string;
            avatar?: string;
            role?: string;
            secondaryRole?: string | null;
        };
        isPinned: boolean;
        approvalStatus?: 'pending' | 'approved' | 'rejected';
        approvedBy?: string | null;
        approvedAt?: string | null;
        viewCount: number;
        likeCount: number;
        commentCount: number;
        createdAt: string;
        image?: string;
    };
    variant?: 'list' | 'grid';
    currentCategory?: string; // Category from URL to preserve in navigation
}

const ForumPostCard: React.FC<ForumPostCardProps> = ({ news, variant = 'list', currentCategory }) => {
    const navigate = useNavigate();
    const [baseUrl, setBaseUrl] = React.useState('');

    React.useEffect(() => {
        import('../utils/api').then(({ getApiBaseUrl }) => {
            const apiUrl = getApiBaseUrl();
            setBaseUrl(apiUrl.split('/api')[0]);
        });
    }, []);

    const categoryColors: Record<string, string> = {
        bqt: 'from-red-600 to-pink-600',
        bcs: 'from-blue-600 to-cyan-600',
        general: 'from-green-500 to-emerald-500',
        // Fallback for legacy data
        announcement: 'from-red-500 to-pink-500',
        study: 'from-blue-500 to-cyan-500',
        event: 'from-purple-500 to-indigo-500',
    };

    const categoryLabels: Record<string, string> = {
        bqt: 'Thông Báo',
        bcs: 'Lớp Học',
        general: 'Thảo Luận',
        // Fallback for legacy data
        announcement: 'Thông báo',
        study: 'Học tập',
        event: 'Sự kiện',
    };

    const statusConfig = {
        pending: {
            label: 'Chờ duyệt',
            color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400',
            icon: '⏳'
        },
        approved: {
            label: 'Đã duyệt',
            color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
            icon: '✅'
        },
        rejected: {
            label: 'Từ chối',
            color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400',
            icon: '❌'
        }
    };

    // Helper to clean content for preview (Strip HTML & Markdown)
    const cleanContent = (content: string) => {
        if (!content) return '';
        try {
            // 1. Decode HTML entities (if backend sends escaped HTML)
            // Using a temporary DOM element to handle entities safely
            const txt = document.createElement("textarea");
            txt.innerHTML = content;
            let text = txt.value;

            // 2. Remove HTML tags
            text = text.replace(/<[^>]*>?/gm, ' ');

            // 3. Strip Markdown
            return text
                .replace(/^#+\s+/gm, '') // Remove headers start
                .replace(/[#*`_~]/g, '') // Remove simple markers
                .replace(/!\[([^\]]*)\]\([^\)]+\)/g, '') // Remove images
                .replace(/\[([^\]]+)\]\([^\)]+\)/g, '$1') // Remove links
                .replace(/^\s*-\s+/gm, '') // Remove list bullets
                .replace(/^\s*\d+\.\s+/gm, '') // Remove list numbers
                .replace(/^\s*>\s+/gm, '') // Remove blockquotes
                .replace(/\n+/g, ' ') // Replace newlines with space
                .replace(/\s+/g, ' ') // Collapse multiple spaces
                .trim();
        } catch (e) {
            return content;
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = now.getTime() - date.getTime();
        const days = Math.floor(diff / (1000 * 60 * 60 * 24));

        if (days === 0) return 'Hôm nay';
        if (days === 1) return 'Hôm qua';
        if (days < 7) return `${days} ngày trước`;
        return date.toLocaleDateString('vi-VN');
    };

    const getImageUrl = () => {
        if (!news.image) return '';
        if (news.image.startsWith('http')) return news.image;
        if (news.image.startsWith('data:')) return news.image;
        return baseUrl ? `${baseUrl}${news.image.startsWith('/') ? news.image : '/' + news.image}` : '';
    };

    const isList = variant === 'list';

    return (
        <div
            onClick={() => {
                const url = currentCategory
                    ? `/forum/${news.id}?category=${currentCategory}`
                    : `/forum/${news.id}`;
                navigate(url);
            }}
            className="
                group relative bg-white dark:bg-gray-800 rounded-lg overflow-hidden
                border border-gray-200 dark:border-gray-700
                hover:border-blue-500 dark:hover:border-blue-500
                hover:shadow-md hover:bg-gray-50 dark:hover:bg-gray-750
                transition-all duration-200 cursor-pointer
                p-2.5
            "
        >
            <div className="flex flex-col gap-1.5">
                {/* 1. Header: Author Info */}
                <div className="flex items-center gap-2">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                        {news.author?.avatar ? (
                            <img
                                src={news.author.avatar}
                                alt={news.author.name}
                                className="w-8 h-8 rounded-full object-cover ring-2 ring-white dark:ring-gray-700"
                            />
                        ) : (
                            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white text-[10px] font-bold ring-2 ring-white dark:ring-gray-700">
                                {news.author?.name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                        )}
                    </div>

                    {/* Text Meta */}
                    <div className="flex flex-col min-w-0 justify-center">
                        {/* Row 1: Name + Verify + Roles */}
                        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-0.5">
                            <div className="flex items-center gap-1">
                                <span className="font-bold text-sm text-gray-900 dark:text-gray-100 line-clamp-1">
                                    {news.author?.name || 'Unknown'}
                                </span>
                                <VerifiedBadge role={news.author?.role} />
                            </div>

                            <div className="flex items-center gap-1 scale-90 origin-left">
                                {news.author?.role && <RoleBadge role={news.author.role} size="sm" />}
                                {news.author?.secondaryRole && <RoleBadge role={news.author.secondaryRole} size="sm" />}
                            </div>
                        </div>
                    </div>
                </div>

                {/* 2. Body: Badges & Title */}
                <div>
                    <div className="flex items-center gap-1.5 mb-1">
                        {/* Approval Status Badge */}
                        {news.approvalStatus && news.approvalStatus !== 'approved' && (
                            <span className={`
                                inline-block px-1.5 py-0.5 rounded-[4px] text-[10px] font-semibold
                                ${statusConfig[news.approvalStatus].color}
                                border border-current/20 leading-none
                            `}>
                                {statusConfig[news.approvalStatus].icon} {statusConfig[news.approvalStatus].label}
                            </span>
                        )}

                        {/* Pinned Badge */}
                        {news.isPinned && (
                            <div className="flex items-center gap-1 text-red-500 bg-red-50 dark:bg-red-900/20 px-1.5 py-0.5 rounded-[4px] text-[10px] font-bold border border-red-100 dark:border-red-900/30 leading-none">
                                <FaThumbtack size={8} />
                                <span className="uppercase">Ghim</span>
                            </div>
                        )}
                    </div>

                    {/* Title */}
                    <h3 className="font-bold text-gray-900 dark:text-white mb-1 line-clamp-2 group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors text-[15px] leading-snug">
                        {cleanContent(news.title)}
                    </h3>
                </div>

                {/* 3. Footer: Stats & Date */}
                <div className="flex items-center justify-between pt-1.5 border-t border-gray-100 dark:border-gray-700/50 mt-0.5">
                    {/* Left: Stats */}
                    <div className="flex items-center gap-3 text-[11px] text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1" title="Lượt xem">
                            <FaEye className="text-gray-400" />
                            <span className="font-medium">{news.viewCount || 0}</span>
                        </div>
                        <div className="flex items-center gap-1" title="Lượt thích">
                            <FaHeart className="text-gray-400" />
                            <span className="font-medium">{news.likeCount || 0}</span>
                        </div>
                        <div className="flex items-center gap-1" title="Bình luận">
                            <FaComment className="text-gray-400" />
                            <span className="font-medium">{news.commentCount || 0}</span>
                        </div>
                    </div>

                    {/* Right: Date */}
                    <div className="text-[11px] text-gray-400 dark:text-gray-500 font-medium whitespace-nowrap">
                        <span>{formatDate(news.createdAt)}</span>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ForumPostCard;
